
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import "../Style/VehiclesTable.css";

// --- Icon Components ---
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const CloudUploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
  </svg>
);

const VehiclesTable = () => {
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [userRole, setUserRole] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const perPage = 10;

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    setUserRole(role);
  }, []);

  // --- 1. Fetch Customers List (For SuperAdmin Dropdown) ---
  const fetchCustomers = async () => {
    const token = localStorage.getItem("token");
    const response = await fetch(`https://api.yellowmatrix.co.in/customers`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch customers");
    }
    return response.json();
  };

  const { data: customersList = [], isLoading: isLoadingCustomers } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
    // Only fetch customers if the user is a SuperAdmin
    enabled: userRole === "SuperAdmin",
  });


  // --- 2. Fetch Devices (With Backend Filtering) ---
  const fetchDevices = async (currentPage, customerId) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authentication token not found.");

    // Append customer_id to the URL if one is selected
    let url = `https://api.yellowmatrix.co.in/vehiclereg/assigned?page=${currentPage}&per_page=${perPage}`;
    if (customerId) {
      url += `&customer_id=${encodeURIComponent(customerId)}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to fetch devices: ${response.status} - ${errorData.message || "Unknown error"}`
      );
    }

    const data = await response.json();
    setPagination(data.Pagination);
    return data.Vehicle || [];
  };

  const {
    data: currentData = [],
    isLoading: isLoadingDevices,
    isError,
    error,
  } = useQuery({
    // Add selectedCustomer to queryKey so it refetches automatically when the dropdown changes
    queryKey: ["devices", page, selectedCustomer], 
    queryFn: () => fetchDevices(page, selectedCustomer),
  });

  // Reset to page 1 when the user selects a new customer from the dropdown
  useEffect(() => {
    setPage(1);
  }, [selectedCustomer]);

  if (isError) {
    return (
      <div className="vehicles-card">
        <h2 className="vehicles-title">Vehicles</h2>
        <p className="subtitle-FirmwareOverAir">
          Manage and monitor your firmware rollouts.
        </p>
        <hr className="vehicles-line" width="100%" />
        <div className="error-message-vehicles">Error: {error.message}</div>
      </div>
    );
  }

  const headers = [
    { key: "VehicleNumber", label: "Vehicle Number" },
    { key: "TotalDrivingTime", label: "Total Driving Time" },
    { key: "IdlingTime", label: "Idling Time" },
    { key: "HaltTime", label: "Halt Time" },
    { key: "TotalDistance", label: "Total Distance" },
    { key: "DriveAlert", label: "Drive Alert" },
    { key: "VIMNumber", label: "VIM Number" },
    { key: "UniqueDeviceId", label: "Unique Device Id" },
    { key: "IMSI", label: "IMSI" },
    { key: "ICCID", label: "ICCID" },
    { key: "CustomIdentification", label: "Custom Identification" },
  ];

  const handlePrevPage = () => {
    if (pagination.HasPrev) setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (pagination.HasNext) setPage((prev) => prev + 1);
  };

  const handleCustomerChange = (e) => {
    setSelectedCustomer(e.target.value);
  };

  const getDeviceValue = (device, key) => {
    return device[key] ?? "N/A";
  };

  return (
    <div className="vehicles-card">
      <h2 className="vehicles-title">Vehicles</h2>
      <p className="vehicles-subtitle">
        Centralized access to vehicle information, connectivity, and tracking data.
      </p>
      <hr className="vehicles-line" width="100%" />

      {userRole === "SuperAdmin" && (
        <div className="vendor-select-container">
          <label htmlFor="vendor-select" className="vendor-select-label">
            Choose Customer
          </label>
          <select
            id="vendor-select"
            value={selectedCustomer}
            onChange={handleCustomerChange}
            className="vendor-select"
            disabled={isLoadingDevices || isLoadingCustomers}
          >
            <option value="">
              {isLoadingCustomers ? "Loading Customers..." : "All Customers"}
            </option>
            {/* Populate dropdown using the /customers API endpoint */}
            {!isLoadingCustomers &&
              customersList.map((customer) => (
                <option key={customer.CustomerID} value={customer.CustomerID}>
                  {customer.CustomerName || customer.CustomerID}
                </option>
              ))}
          </select>
        </div>
      )}

      <div className="vehicles-actions">
        <button className="action-button" aria-label="Search" disabled={isLoadingDevices}>
          <SearchIcon />
        </button>
        <button className="action-button" aria-label="Upload" disabled={isLoadingDevices}>
          <CloudUploadIcon />
        </button>
      </div>

      {/* Desktop Table */}
      <div className="vehicle-table-container">
        <table className="vehicles-table">
          <thead className="vehicles-table-header">
            <tr>
              {headers.map((header) => (
                <th key={header.key} className="table-header-cell">
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoadingDevices ? (
              [...Array(perPage)].map((_, index) => (
                <tr key={index} className="table-row skeleton-loader-2">
                  {headers.map((_, cellIndex) => (
                    <td key={cellIndex} className="table-body-cell">
                      <div className="skeleton-line-2"></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : currentData.length > 0 ? (
              currentData.map((device, index) => (
                <tr key={index} className="table-row">
                  {headers.map((header) => (
                    <td key={`${index}-${header.key}`} className="table-body-cell">
                      {getDeviceValue(device, header.key)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} className="no-data-message">
                  No vehicles found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        {isLoadingDevices ? (
          [...Array(perPage)].map((_, index) => (
            <div key={index} className="mobile-card skeleton-loader-2">
              {headers.map((header, cellIndex) => (
                <div key={cellIndex} className="mobile-card-item">
                  <div className="mobile-card-label">{header.label}</div>
                  <div className="mobile-card-value">
                    <div className="skeleton-line-2"></div>
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : currentData.length > 0 ? (
          currentData.map((device, index) => (
            <div key={index} className="mobile-card">
              {headers.map((header) => (
                <div key={`${index}-${header.key}`} className="mobile-card-item">
                  <div className="mobile-card-label">{header.label}</div>
                  <div className="mobile-card-value">
                    {getDeviceValue(device, header.key)}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="no-data-message">No vehicles found</div>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        {isLoadingDevices ? (
          <span className="pagination-info skeleton-loader-text-2">
            <div className="skeleton-line-2" style={{ width: "120px" }}></div>
          </span>
        ) : currentData.length > 0 ? (
          <span className="pagination-info">
            Page {pagination.Page} of {pagination.TotalPages} | Total Records:{" "}
            {pagination.TotalRecords}
          </span>
        ) : (
          <span className="pagination-info">No vehicles to show.</span>
        )}
        <div className="pagination-buttons">
          <button
            className="pagination-button"
            onClick={handlePrevPage}
            disabled={!pagination.HasPrev || isLoadingDevices}
          >
            Previous
          </button>

          <button
            className="pagination-button"
            onClick={handleNextPage}
            disabled={!pagination.HasNext || isLoadingDevices}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehiclesTable;